//
// Copyright © 2026 PlayJoy Studios.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

import type { AiLevel, Conversation } from './index'
import type { ModelLevel } from './rest'

/**
 * @public
 */
export const aiLevels: AiLevel[] = ['low', 'medium', 'high']

/**
 * @public
 */
export function isAiLevel (value: unknown): value is AiLevel {
  return typeof value === 'string' && (aiLevels as string[]).includes(value)
}

/**
 * @public
 */
export function findModelLevel (models: ModelLevel[], level: unknown): ModelLevel | undefined {
  return isAiLevel(level) ? models.find((it) => it.level === level) : undefined
}

/**
 * @public
 */
export function levelOfConversation (
  models: ModelLevel[],
  conversation: Pick<Conversation, 'model' | 'effort'> | undefined,
  fallback: AiLevel
): AiLevel {
  if (conversation === undefined) {
    return fallback
  }
  // The stored conversation knows the model and the effort, not the level itself.
  const exact = models.find((it) => it.model === conversation.model && it.effort === conversation.effort)
  if (exact !== undefined) {
    return exact.level
  }
  return models.find((it) => it.model === conversation.model)?.level ?? fallback
}

/**
 * @public
 */
export function levelTitle (name: string, model: string | undefined): string {
  return model !== undefined && model !== '' ? name + ' (' + model + ')' : name
}
